import React from 'react';
import { LocalAudioTrack } from 'twilio-video';
import { MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from 'assets';
import { useDevices, useLocalStorageState, useMediaStreamTrack, useVideo } from 'hooks';

import { SELECTED_AUDIO_INPUT_KEY } from '../../../constants';

type AudioInputListProps = {
  forceLightTheme?: boolean;
};

export function AudioInputList({ forceLightTheme }: AudioInputListProps) {
  const { t } = useTranslation('providerRoom');
  const { audioInputDevices } = useDevices();
  const { localTracks } = useVideo();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSelectedAudioInputDevice] = useLocalStorageState<string>(SELECTED_AUDIO_INPUT_KEY, 'default');

  const localAudioTrack = localTracks.find((track) => track.kind === 'audio') as LocalAudioTrack;
  const mediaStreamTrack = useMediaStreamTrack(localAudioTrack);
  const localAudioInputDeviceId = mediaStreamTrack?.getSettings().deviceId;

  function replaceTrack(newDeviceId: string) {
    setSelectedAudioInputDevice(newDeviceId);
    localAudioTrack?.restart({ deviceId: { exact: newDeviceId } });
  }

  const isDisabled = audioInputDevices.length <= 1;

  return (
    <>
      <Select
        fullWidth
        variant='outlined'
        disabled={isDisabled}
        IconComponent={ChevronRightIcon}
        value={!isDisabled ? localAudioInputDeviceId || '' : 'default'}
        onChange={(e) => replaceTrack(e.target.value as string)}
        MenuProps={
          forceLightTheme
            ? {
                PaperProps: {
                  sx: {
                    backgroundColor: '#ffffff',
                    color: '#393534',
                    '& .MuiMenuItem-root': {
                      color: '#393534',
                    },
                  },
                },
              }
            : undefined
        }
        sx={{
          ...(forceLightTheme && {
            backgroundColor: '#ffffff !important',
            color: '#393534',
            '& .MuiSelect-select': {
              backgroundColor: '#ffffff !important',
              color: '#393534 !important',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23) !important',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23) !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23) !important',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f3f3f3 !important',
              '& .MuiSelect-select': {
                backgroundColor: '#f3f3f3 !important',
                WebkitTextFillColor: '#626363',
              },
            },
            '& .MuiSelect-icon': {
              color: '#626363',
            },
          }),
          '& svg': {
            width: '10px',
          },
        }}
      >
        {!isDisabled ? (
          audioInputDevices.map((device) => (
            <MenuItem value={device.deviceId} key={device.deviceId}>
              {device.label}
            </MenuItem>
          ))
        ) : (
          <MenuItem value='default'>{localAudioTrack?.mediaStreamTrack.label || t('noLocalAudio')}</MenuItem>
        )}
      </Select>
    </>
  );
}
